import { observer } from 'mobx-react';
import { useCallback } from 'react';
import FilterButton from '../../FilterButton';
import { useAdvancedFilterContext } from '../../context';
import { AdvancedFilterProperties, NotBooleanProperties } from '../../types';
import { Icons } from 'src/assets/fonts/Icons/icons';
import { localeStore } from 'src/mobx/localesStore';
import { TBooleanInNumber } from 'src/types/main';
import { formatImagePath } from 'src/utils/formatter';
import { TOOLTIP_DURATION, TOOLTIP_POSITION } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Icon from 'src/view/components/Controls/Icon/Icon';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import TooltipIcon from '~/view/components/TooltipIcon/TooltipIcon';
import './styles.scss';

interface MainFilterModalProps {
  onClose: () => void;
  onChangeModal: (property: AdvancedFilterProperties) => void;
}

const MainFilterModal = ({ onChangeModal, onClose }: MainFilterModalProps) => {
  const { store } = useAdvancedFilterContext();

  const handleApplyFilter = useCallback(() => {
    store.applyFilter();
    onClose();
  }, [onClose, store]);

  return (
    <div className="advanced-main-filter-modal">
      <ModalHeader title={localeStore.t('filters')} />
      <div className="close-icon mt-2 mr-2" onClick={onClose}>
        <Icon name={Icons.Close} size={10} />
      </div>
      <div className="d-flex flex-1 px-8 py-4 flex-column ">
        {store.properties.map((property, i) => (
          <div key={i} className="property cursor-pointer" onClick={() => onChangeModal(property.propertyKey)}>
            <div className=" d-flex flex-1 text-wrapper">
              <p className="text">{localeStore.t(property.title)}</p>
              <p className="second-text pl-1">{property.subText}</p>
            </div>
            {!!property.subText && (
              <div
                className="px-2 reset-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  store.resetPropertyValues(property.propertyKey as NotBooleanProperties);
                }}
              >
                <img src={formatImagePath(`${Icons.CloseCircle}.svg`)} alt="Close" style={{ width: 12, height: 12 }} />
              </div>
            )}
            <Icon name={Icons.ExpandRight} className="color-gray2 pagination__button-arrow" />
          </div>
        ))}
        {store.booleanProperties.length > 0 && <hr className="filter-separator" />}
        {store.booleanProperties.map((booleanProperty, i) => (
          <div className="boolean-property" key={i}>
            <div className="boolean-property__text gap-10">
              {localeStore.t(booleanProperty.title)}
              <TooltipIcon
                message={localeStore.t(booleanProperty.info.on) + '\n\n' + localeStore.t(booleanProperty.info.off)}
                duration={TOOLTIP_DURATION.EXTRA_LARGE}
                position={TOOLTIP_POSITION.PORTAL}
                maxWidth={280}
                gapTop={0}
                messageClassName="advanced_filter__toggle-tooltip"
              />
            </div>
            <Switcher
              status={Number(booleanProperty.checked) as TBooleanInNumber}
              onChange={(status) => {
                store.toggleBooleanProperty(booleanProperty.key, Boolean(status));
              }}
            />
          </div>
        ))}
      </div>
      <div className="advanced-filter-buttons">
        <FilterButton title="apply" onClick={handleApplyFilter} variant="primary" />
        <FilterButton title="txt_clear" onClick={store.resetAllValues} />
      </div>
    </div>
  );
};

export default observer(MainFilterModal);
